import { Component, EventEmitter, Output, OnInit, OnDestroy, HostListener, ViewChild, ElementRef, inject, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';
import { AuthService } from '@ui-nuvertos/auth';
import { GlobalSearchService, SearchResult, ModuleOption, FeatureResult, DocumentNavigationService } from '@ui-nuvertos/shared';

// PrimeNG Imports
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { BadgeModule } from 'primeng/badge';
import { AvatarModule } from 'primeng/avatar';
import { MenuModule } from 'primeng/menu';
import { PopoverModule, Popover } from 'primeng/popover';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MenuItem } from 'primeng/api';

@Component({
  selector: 'ui-topbar',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule,
    ButtonModule, 
    InputTextModule,
    BadgeModule, 
    AvatarModule,
    MenuModule,
    PopoverModule,
    ProgressSpinnerModule
  ],
  templateUrl: './topbar.html',
  styleUrl: './topbar.scss',
  encapsulation: ViewEncapsulation.None
})
export class TopbarComponent implements OnInit, OnDestroy {
  @Output() toggleSidebar = new EventEmitter<void>();
  
  // ViewChild to access search input and overlay directly
  @ViewChild('searchInput', { static: false }) searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('searchResultsOverlay', { static: false }) searchResultsOverlay!: Popover;
  @ViewChild('searchAnchor', { static: false }) searchAnchor!: ElementRef<HTMLDivElement>;
  observer?: ResizeObserver;

  // User menu
  userMenuItems: MenuItem[] = [
    {
      label: 'Profile',
      icon: 'pi pi-user',
      command: () => this.goToProfile()
    },
    {
      label: 'Settings',
      icon: 'pi pi-cog',
      command: () => this.goToSettings()
    },
    {
      separator: true
    },
    {
      label: 'Logout',
      icon: 'pi pi-sign-out',
      command: () => this.onLogout()
    }
  ];

  // Search properties
  searchQuery: string = '';
  isSearchFocused: boolean = false;
  searchResults: SearchResult[] = [];
  featureResults: FeatureResult[] = [];
  totalCount: number = 0;
  isSearchLoading: boolean = false;
  selectedResultIndex: number = -1;
  showModuleSuggestions: boolean = true;
  moduleOptions: ModuleOption[] = [];

  // User properties
  notificationCount: number = 3;
  userImageSrc: string = 'https://cdn.pixabay.com/photo/2023/02/18/11/00/icon-7797704_640.png';
  userName: string = '';
  userEmail: string = '';
  userRole: string = '';

  isNotificationHovered: boolean = false;
  isProfileHovered: boolean = false;
  isProfileMenuOpen: boolean = false;
  showBackdrop: boolean = false;

  get showProfileActive(): boolean {
    return this.isProfileHovered || this.isProfileMenuOpen;
  }

  private router = inject(Router);
  private authService = inject(AuthService);
  private globalSearchService = inject(GlobalSearchService);
  private documentNavigation = inject(DocumentNavigationService);
  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  ngOnInit(): void {
    console.log('NuvertOS Topbar initialized');
    
    // Get module options from service
    this.moduleOptions = this.globalSearchService.moduleOptions;
    
    // Setup debounced search
    this.setupSearchSubscription();
    
    // Subscribe to user session changes to update user name
    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        if (user) {
          console.log("USER", user);
          // Try different possible field names for the user's display name
          const userAny = user as any;
          const displayName = userAny.name
            || userAny.username
            || userAny.full_name
            || userAny.first_name
            || (user.email ? user.email.split('@')[0] : null)
            || 'User';
          this.userName = displayName;
          this.userEmail = user.email || '';
          this.userRole = userAny.role || userAny.designation || userAny.department || '';
        } else {
          this.userName = '';
          this.userEmail = '';
          this.userRole = '';
        }
      });
    
    // If user is null initially, try to load the session
    if (!this.authService.getCurrentUser()) {
      this.authService.loadUserSession().pipe(takeUntil(this.destroy$)).subscribe();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  fixSearchPosition() {
    this.showBackdrop = true;
    const overlay = document.querySelector('.global-search-overlay') as HTMLElement;
    if (!overlay) return;

    const recalculatePosition = () => {
      const anchorRect = this.searchAnchor.nativeElement.getBoundingClientRect();
      const overlayRect = overlay.getBoundingClientRect();
      overlay.style.left =
        `${anchorRect.left + anchorRect.width / 2 - overlayRect.width / 2}px`;
      overlay.style.top =
        `${anchorRect.bottom + 8}px`;
    }

    recalculatePosition();

    this.observer?.disconnect();
    this.observer = new ResizeObserver(recalculatePosition);
    this.observer.observe(overlay);
  }

  onHide() {
    this.showBackdrop = false;
    this.observer?.disconnect();
    this.observer = undefined;
  }

  private setupSearchSubscription(): void {
    this.searchSubject.pipe(
      debounceTime(300),
      // Only skip if both are the same non-empty query (allows re-searching after clear)
      distinctUntilChanged((prev, curr) => prev === curr && prev.length >= 2),
      switchMap(query => {
        if (!query || query.trim().length < 2) {
          this.showModuleSuggestions = true;
          this.featureResults = [];
          return of({ results: [], total_count: 0, query: '' });
        }
        this.isSearchLoading = true;
        this.showModuleSuggestions = false;
        
        // Search features locally (synchronous)
        this.featureResults = this.globalSearchService.searchFeatures(query, 5);
        
        // Search entities via API
        return this.globalSearchService.search(query).pipe(
          catchError(() => of({ results: [], total_count: 0, query: '' }))
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.searchResults = response.results || [];
        this.totalCount = response.total_count || 0;
        this.isSearchLoading = false;
        this.selectedResultIndex = -1;
      },
      error: () => {
        this.isSearchLoading = false;
        this.searchResults = [];
        this.totalCount = 0;
      }
    });
  }

  // Fixed HostListener - using standard keydown event
  @HostListener('window:keydown', ['$event'])
  handleGlobalKeydown(event: KeyboardEvent): void {
    // Handle Escape key
    if (event.key === 'Escape') {
      if (this.isSearchFocused) {
        this.closeSearchOverlay();
        event.preventDefault();
      }
    }
    
    // Handle Cmd+K or Ctrl+K for search
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      this.focusSearchInput();
    }
    
    // Handle Cmd+/ or Ctrl+/ for help
    if ((event.metaKey || event.ctrlKey) && event.key === '/') {
      event.preventDefault();
      console.log('Help shortcut - implement help modal');
    }
  }

  onToggleSidebar(): void {
    this.toggleSidebar.emit();
  }

  onSearchInput(): void {
    // Ensure overlay is visible when typing
    if (this.isSearchFocused && this.searchResultsOverlay && this.searchInput?.nativeElement) {
      this.searchResultsOverlay.show(null, this.searchInput.nativeElement);
    }
    this.searchSubject.next(this.searchQuery);
  }

  onSearch(): void {
    if (this.searchQuery.trim()) {
      // If there's a selected result, navigate to it
      if (this.selectedResultIndex >= 0 && this.selectedResultIndex < this.searchResults.length) {
        this.navigateToResult(this.searchResults[this.selectedResultIndex]);
      } else if (this.searchResults.length > 0) {
        // Navigate to first result
        this.navigateToResult(this.searchResults[0]);
      }
    }
  }

  onSearchKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        const totalItems = this.featureResults.length + this.searchResults.length;
        if (totalItems > 0) {
          this.selectedResultIndex = Math.min(this.selectedResultIndex + 1, totalItems - 1);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        const totalCount = this.featureResults.length + this.searchResults.length;
        if (totalCount > 0) {
          this.selectedResultIndex = Math.max(this.selectedResultIndex - 1, -1);
        }
        break;
      case 'Enter':
        event.preventDefault();
        if (this.selectedResultIndex !== -1) {
          this.onSearchSelection(event);
        } else {
          this.onSearch();
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.closeSearchOverlay();
        break;
    }
  }

  onSearchFocus(event: Event): void {
    this.isSearchFocused = true;
    // Show overlay when focusing on search
    if (this.searchResultsOverlay && this.searchInput?.nativeElement) {
      this.searchResultsOverlay.show(event, this.searchInput.nativeElement);
    }
    // If query is empty, show module suggestions
    if (!this.searchQuery || this.searchQuery.trim().length < 2) {
      this.showModuleSuggestions = true;
    }
  }

  onSearchBlur(): void {
    // Delay blur handling to allow click on results
    setTimeout(() => {
      this.isSearchFocused = false;
    }, 200);
  }

  focusSearchInput(): void {
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.focus();
      this.searchInput.nativeElement.select();
      this.isSearchFocused = true;
    }
  }

  closeSearchOverlay(): void {
    this.searchQuery = '';
    this.searchSubject.next('');
    this.searchResults = [];
    this.totalCount = 0;
    this.selectedResultIndex = -1;
    this.showModuleSuggestions = true;
    this.isSearchFocused = false;
    if (this.searchResultsOverlay) {
      this.searchResultsOverlay.hide();
    }
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.blur();
    }
  }

  clearSearchAndBlur(): void {
    this.closeSearchOverlay();
  }

  onSearchSelection(event: KeyboardEvent): void {
    if (this.selectedResultIndex < this.featureResults.length) {
      // Feature selection
      const feature = this.featureResults[this.selectedResultIndex];
      // Pass a mock MouseEvent if needed or update navigateToFeature to handle KeyboardEvent/modifiers check manually
      // Since event is KeyboardEvent here, we manually check modifier keys
      const isNewTab = event.metaKey || event.ctrlKey;
      this.closeSearchOverlay();
      if (isNewTab) {
        const baseUrl = window.location.origin;
        window.open(`${baseUrl}${feature.route}`, '_blank');
      } else {
        this.router.navigateByUrl(feature.route);
      }
    } else {
      // Entity selection
      const resultIndex = this.selectedResultIndex - this.featureResults.length;
      if (resultIndex >= 0 && resultIndex < this.searchResults.length) {
        const result = this.searchResults[resultIndex];
        // Create a mock mouse event for navigateToResult to check keys
        // Or better, update navigateToResult to accept keys directly? 
        // navigateToResult checks event?.metaKey. KeyboardEvent has these props too.
        // Navigate to result expects MouseEvent, but KeyboardEvent has compatible modifier props.
        // We can cast or adjust the signature. Let's cast for now as it's cleaner than duplicating logic.
        this.navigateToResult(result, event as any);
      }
    }
  }

  // Module selection
  selectModule(module: ModuleOption): void {
    this.searchQuery = module.prefix + ' ';
    this.showModuleSuggestions = false;
    // Focus and put cursor at end
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.focus();
      const length = this.searchQuery.length;
      this.searchInput.nativeElement.setSelectionRange(length, length);
    }
  }

  // Result navigation - now uses DocumentNavigationService
  navigateToResult(result: SearchResult, event?: MouseEvent): void {
    // Detect modifier keys for different navigation modes
    const isNewTab = event?.metaKey || event?.ctrlKey;
    const isEditMode = event?.shiftKey;
    const mode = isEditMode ? 'edit' : 'view';

    // Map entity type to DocumentType
    const documentType = this.documentNavigation.mapEntityTypeToDocumentType(result.entity_type);
    
    if (documentType) {
      this.closeSearchOverlay();
      this.documentNavigation.navigateTo(documentType, result.entity_id, {
        mode,
        newTab: isNewTab
      });
    } else {
      console.warn(`Unsupported entity type for navigation: ${result.entity_type}`);
    }
  }

  // Navigate to a feature/navigation item
  navigateToFeature(feature: FeatureResult, event?: MouseEvent): void {
    const isNewTab = event?.metaKey || event?.ctrlKey;
    this.closeSearchOverlay();
    
    if (isNewTab) {
      const baseUrl = window.location.origin;
      window.open(`${baseUrl}${feature.route}`, '_blank');
    } else {
      this.router.navigateByUrl(feature.route);
    }
  }

  onResultHover(index: number): void {
    this.selectedResultIndex = index;
  }

  // Helper methods for template
  getEntityIcon(entityType: string): string {
    return this.globalSearchService.getEntityIcon(entityType);
  }

  getEntityColor(entityType: string): string {
    return this.globalSearchService.getEntityColor(entityType);
  }

  formatEntityType(entityType: string): string {
    return this.globalSearchService.formatEntityType(entityType);
  }

  getStatusClass(status: string): string {
    return this.globalSearchService.getStatusClass(status);
  }

  formatStatus(status: string): string {
    return this.globalSearchService.formatStatus(status);
  }

  formatAmount(amount: number | undefined): string {
    return this.globalSearchService.formatAmount(amount);
  }

  goToProfile(): void {
    this.router.navigate(['/profile']);
  }

  goToSettings(): void {
    this.router.navigate(['/settings']);
  }

  onLogout(): void {
    // Wait for logout API call to complete before navigating
    // This ensures server-side session is cleared
    this.authService.logout().subscribe({
      next: () => {
        // Logout API call succeeded, navigate to login
        this.router.navigate(['/login']);
      },
      error: (error) => {
        // Even if logout API fails, navigate to login
        // Local state has already been cleared by authService
        console.warn('[TopbarComponent] Logout API error, but navigating to login:', error);
        this.router.navigate(['/login']);
      },
      complete: () => {
        // Ensure navigation happens even if next/error handlers don't execute
        // This is a safety net
        if (!this.router.url.includes('/login')) {
          this.router.navigate(['/login']);
        }
      }
    });
  }

  onNotificationClick(): void {
    console.log('Notifications clicked');
    // Handle notification click - open notification panel, etc.
  }

  onNotificationHover(isHovered: boolean): void {
    this.isNotificationHovered = isHovered;
    if (isHovered) {
      this.isProfileHovered = false; // Ensure profile hover is reset
    }
  }
  
  onProfileHover(isHovered: boolean): void {
    this.isProfileHovered = isHovered;
    if (isHovered) {
      this.isNotificationHovered = false; // Ensure notification hover is reset
    }
  }
}
